// CR081: AI Deep Scan Diagnostics Panel
// CR089: Enhanced with per-page results, raw AI output view, and proof artifacts
// CR090: Added View Deal Types modal, strict deal type taxonomy
// CR226: Removed Prompt Pack override dropdown - pack is now read-only from S0 Scan Settings
// Shows live progress, per-page results, and deal extraction details

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { 
  ChevronDown, Clock, CheckCircle, AlertCircle, Loader2, Zap, XCircle, ExternalLink, Eye, FileText, ListChecks
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { DEAL_TYPE_REGISTRY, type DealCategory } from "@/lib/deals/dealTypeRegistry";

// CR090: Prompt Pack type for dropdown
interface PromptPack {
  id: string;
  name: string;
  version: number;
  is_active: boolean;
}

// CR092: Enhanced page result with per-AI-call audit
interface PageScanResult {
  page_number: number;
  page_image_url_used: string;
  page_storage_path: string;
  ai_status: "SUCCESS" | "FAILED" | "SKIPPED";
  raw_ai_response_json: unknown;
  parsed_deals_count: number;
  staging_deals_created_count: number;
  deal_type_breakdown: Record<string, number>;
  // CR455: Parsed vs Kept breakdowns
  parsed_deal_type_breakdown?: Record<string, number>;
  kept_deals_count?: number;
  kept_deal_type_breakdown?: Record<string, number>;
  error?: string;
  duration_ms: number;
  model_used: string;
  prompt_pack_id_used: string | null;
  prompt_pack_version_used: string | null;
  // CR092: Per-AI-call audit fields
  ai_call_id?: string;
  prompt_step?: string;
  input_tokens?: number;
  output_tokens?: number;
  // CR453: Per-page rejection tracking
  rejected_count?: number;
  reject_reasons?: string[];
  // CR454: Printed page number
  printed_page_number?: number | null;
}

// CR092: Enhanced error structure for actionable diagnostics
interface StepError {
  code: string;
  message: string;
  step_name: string;
  timestamp: string;
  flyer_id?: string;
  flyer_merchant?: string;
  page_number?: number;
  ai_call_id?: string;
  stack_trace?: string;
}

// CR089: Enhanced diagnostics with per-page results and proof artifacts
// CR092: Added reconciliation tracking and enhanced error structure
// CR451: Added summary_v2 support
interface SummaryV2 {
  schema_version: string;
  extracted_count: number;
  inserted_count: number;
  rejected_count: number;
  deal_types: string[];
  rejections_by_reason: Record<string, number>;
  pages?: Array<{
    pipeline_page_number: number;
    deal_type_breakdown: Record<string, number>;
    // CR455: Parsed vs Kept
    parsed_deals_count?: number;
    parsed_deal_type_breakdown?: Record<string, number>;
    kept_deals_count?: number;
    kept_deal_type_breakdown?: Record<string, number>;
    inserted_count: number;
    rejected_count: number;
    reject_reasons: string[];
    printed_page_number?: number | null;
  }>;
}

interface AiScanDiagnostics {
  stage?: string;
  candidates_total?: number;
  candidates_processed?: number;
  candidates_failed?: number;
  deals_extracted?: number;
  staging_cards_created?: number;
  // CR092: Reconciliation tracking
  reconciliation?: {
    extracted_deal_count: number;
    inserted_staging_deal_count: number;
    rejected_deal_count: number;
    rejection_reasons: Array<{ reason: string; count: number; sample_page?: number }>;
    mismatch_detected: boolean;
    mismatch_diff?: number;
  };
  page_results?: PageScanResult[];
  timing_ms?: {
    total?: number;
    ai_calls?: number;
    staging_writes?: number;
  };
  ai_model?: string;
  prompt_pack_id_used?: string | null;
  prompt_pack_version_used?: string | null;
  prompt_pack_name?: string;
  last_updated_at?: string;
  started_at?: string;
  ended_at?: string | null;
  status?: "RUNNING" | "COMPLETE" | "COMPLETE_WITH_ERRORS" | "PARTIAL" | "FAILED" | "FAILED_MISMATCH" | "BLOCKED_NO_CANDIDATES";
  // CR092: Enhanced errors with step details
  errors?: StepError[];
  // CR475: Version stamp
  ai_scan_version?: string;
  // CR481: Watchdog auto-finalize
  watchdog_finalized?: boolean;
  watchdog_reason?: string;
  // CR451: Canonical summary v2
  summary_v2?: SummaryV2;
}

interface FlyerAiDiagnosticsPanelProps {
  batchRunId: string;
  isRunning: boolean;
  // CR226: Removed onPromptPackOverride - pack is always from S0 config
}

export function FlyerAiDiagnosticsPanel({ batchRunId, isRunning }: FlyerAiDiagnosticsPanelProps) {
  const navigate = useNavigate();
  const [diagnostics, setDiagnostics] = useState<AiScanDiagnostics | null>(null);
  const [isTableOpen, setIsTableOpen] = useState(false);
  const [selectedPageForRawView, setSelectedPageForRawView] = useState<PageScanResult | null>(null);
  
  // CR226: Prompt Pack state is now read-only from S0 config (no dropdown/override)
  const [s0PromptPackId, setS0PromptPackId] = useState<string | null>(null);
  const [s0PromptPackName, setS0PromptPackName] = useState<string | null>(null);
  const [showDealTypesModal, setShowDealTypesModal] = useState(false);

  // CR226: Fetch S0 prompt pack config (read-only, no override)
  useEffect(() => {
    const fetchS0Config = async () => {
      const { data: batchRun } = await supabase
        .from("cr075_batch_runs")
        .select("config_json")
        .eq("id", batchRunId)
        .single();
      const fe = (batchRun?.config_json as { flyerExtraction?: { promptPackId?: string } })?.flyerExtraction;
      const packId = fe?.promptPackId || null;
      setS0PromptPackId(packId);
      
      if (packId) {
        const { data: pack } = await supabase
          .from("prompt_packs")
          .select("name, version")
          .eq("id", packId)
          .single();
        if (pack) {
          setS0PromptPackName(`${pack.name} v${pack.version}`);
        }
      }
    };
    fetchS0Config();
  }, [batchRunId]);

  // CR482D: Poll for diagnostics AND DB status — DB status is authoritative for spinner
  const [dbAttemptStatus, setDbAttemptStatus] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchDiagnostics = async () => {
      const { data } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("status, diagnostics_json, finished_at")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_AI")
        .not("status", "eq", "SUPERSEDED")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (data) {
        const diagStatus = data.diagnostics_json && typeof data.diagnostics_json === "object"
          ? String((data.diagnostics_json as Record<string, unknown>)?.status ?? "").toUpperCase()
          : "";
        const diagEndedAt = data.diagnostics_json && typeof data.diagnostics_json === "object"
          ? (data.diagnostics_json as Record<string, unknown>)?.ended_at
          : null;
        const effectiveStatus =
          String(data.status ?? "").toUpperCase() === "RUNNING" &&
          ["COMPLETE", "COMPLETE_WITH_ERRORS", "FAILED", "FAILED_MISMATCH", "BLOCKED_NO_CANDIDATES", "CANCELLED"].includes(diagStatus) &&
          typeof diagEndedAt === "string"
            ? diagStatus
            : data.status;

        setDbAttemptStatus(effectiveStatus);
        if (data.diagnostics_json && typeof data.diagnostics_json === "object") {
          const diag = data.diagnostics_json as AiScanDiagnostics;
          // CR482D: Ensure last_updated_at has a fallback from finished_at
          if (!diag.last_updated_at && data.finished_at) {
            diag.last_updated_at = data.finished_at;
          }
          setDiagnostics(diag);
        }
      }
    };

    fetchDiagnostics();
    const intervalId = isRunning ? setInterval(fetchDiagnostics, 2000) : undefined;
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [batchRunId, isRunning]);

  // Safe access to diagnostics values with defaults
  const candidatesTotal = diagnostics?.candidates_total ?? 0;
  // CR478C: Progress fallback — use page_results.length if candidates_processed missing
  const candidatesProcessed = diagnostics?.candidates_processed ?? (Array.isArray(diagnostics?.page_results) ? diagnostics.page_results.length : 0);
  // CR455: Prefer summary_v2.extracted_count → diagnostics.deals_extracted → sum of page_results parsed_deals_count
  const summaryV2 = diagnostics?.summary_v2;
  const pageResults = Array.isArray(diagnostics?.page_results) ? diagnostics.page_results : [];
  const pageResultsParsedSum = pageResults.reduce((sum, pr) => sum + (pr.parsed_deals_count || 0), 0);
  const dealsExtracted = summaryV2?.extracted_count ?? diagnostics?.deals_extracted ?? pageResultsParsedSum;
  const stagingCardsCreated = summaryV2?.inserted_count ?? diagnostics?.staging_cards_created ?? 0;
  const errors = Array.isArray(diagnostics?.errors) ? diagnostics.errors : [];
  const warningErrors = errors.filter((err) => err.code === "NON_WORKING_PAGE_ASSET_ALLOWED");
  const blockingErrors = errors.filter((err) => err.code !== "NON_WORKING_PAGE_ASSET_ALLOWED");
  const timingMs = diagnostics?.timing_ms ?? {};
  const aiModel = diagnostics?.ai_model ?? "";
  const promptPackName = diagnostics?.prompt_pack_name;
  const promptPackId = diagnostics?.prompt_pack_id_used;
  const promptPackVersion = diagnostics?.prompt_pack_version_used;
  // CR482D: DB status is authoritative for spinner; diagnostics status is fallback
  const isTerminalDbStatus = dbAttemptStatus && ["COMPLETE", "COMPLETE_WITH_ERRORS", "FAILED", "FAILED_MISMATCH", "BLOCKED_NO_CANDIDATES"].includes(dbAttemptStatus);
  const status = isTerminalDbStatus ? (dbAttemptStatus as AiScanDiagnostics["status"]) : (diagnostics?.status ?? "RUNNING");

  // CR090: Get canonical deal types from registry
  const canonicalDealTypes = Object.entries(DEAL_TYPE_REGISTRY).map(([key, config]) => ({
    key: key as DealCategory,
    label: config.label,
    ribbonLabel: config.ribbonLabel,
  }));

  // Don't render anything if no diagnostics yet - show read-only S0 config
  if (!diagnostics) {
    return (
      <div className="space-y-3">
        {/* CR226: Read-only prompt pack display from S0 */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Prompt Pack (from S0):</span>
          <Badge variant={s0PromptPackId ? "outline" : "destructive"} className="text-xs">
            {s0PromptPackName || (s0PromptPackId ? `Pack ${s0PromptPackId.slice(0,8)}...` : "Not configured")}
          </Badge>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowDealTypesModal(true)}>
            <ListChecks className="h-3 w-3 mr-1" />
            View Deal Types
          </Button>
        </div>
        
        {/* CR090: Deal Types Modal */}
        <Dialog open={showDealTypesModal} onOpenChange={setShowDealTypesModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Canonical Deal Types (from DB)</DialogTitle>
              <DialogDescription>AI must classify deals into ONLY these types. No new types allowed.</DialogDescription>
            </DialogHeader>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Ribbon</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {canonicalDealTypes.map(dt => (
                  <TableRow key={dt.key}>
                    <TableCell className="font-mono text-xs">{dt.key}</TableCell>
                    <TableCell>{dt.label}</TableCell>
                    <TableCell><Badge variant="outline">{dt.ribbonLabel}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // If we have diagnostics but no meaningful data, show waiting state
  if (candidatesTotal === 0 && candidatesProcessed === 0 && dealsExtracted === 0) {
    return (
      <div className="mt-2 text-sm text-muted-foreground border-t pt-3">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Waiting for AI scan progress…</span>
        </div>
      </div>
    );
  }

  const progressPercent = candidatesTotal > 0 
    ? Math.round((candidatesProcessed / candidatesTotal) * 100)
    : 0;
  
  // CR478C: Fallback chain for "Last update" display
  const lastUpdateSource = diagnostics.last_updated_at || diagnostics.ended_at || diagnostics.started_at;
  const lastUpdateAgo = lastUpdateSource
    ? formatDistanceToNow(new Date(lastUpdateSource), { addSuffix: true })
    : "never";
  
  const formatDuration = (ms: number | undefined) => {
    if (ms === undefined || ms === null) return "—";
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getStatusIcon = (pageStatus: PageScanResult["ai_status"]) => {
    switch (pageStatus) {
      case "SUCCESS": return <CheckCircle className="h-3 w-3 text-green-500" />;
      case "FAILED": return <XCircle className="h-3 w-3 text-destructive" />;
      case "SKIPPED": return <AlertCircle className="h-3 w-3 text-amber-500" />;
      default: return <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />;
    }
  };

  // CR482C: Derive deal_type_breakdown from raw AI response if breakdown is empty
  const getDealTypeBadges = (breakdown: Record<string, number>, rawResponse?: unknown) => {
    let effectiveBreakdown = breakdown;
    // If breakdown empty, try to derive from parsed deals in raw response
    if (Object.keys(effectiveBreakdown).length === 0 && rawResponse) {
      const raw = rawResponse as Record<string, unknown>;
      const deals = (raw as any)?.deals || [];
      if (Array.isArray(deals) && deals.length > 0) {
        effectiveBreakdown = {};
        for (const d of deals) {
          const dt = (d as Record<string, unknown>)?.deal_type as string || "UNCLASSIFIED";
          effectiveBreakdown[dt] = (effectiveBreakdown[dt] || 0) + 1;
        }
      }
    }
    return Object.entries(effectiveBreakdown).map(([type, count]) => (
      <Badge key={type} variant="outline" className="text-[10px] px-1">
        {type}: {count}
      </Badge>
    ));
  };

  return (
    <div className="mt-2 space-y-3 text-sm border-t pt-3">
      {/* Live Progress Header */}
      {status === "RUNNING" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
              <span className="font-medium">
                Processing: {candidatesProcessed}/{candidatesTotal} pages · {dealsExtracted} deals → {stagingCardsCreated} cards
              </span>
            </div>
            <span className="text-muted-foreground text-xs flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Last update: {lastUpdateAgo}
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>
      )}

      {/* Completed Summary */}
      {status !== "RUNNING" && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="bg-muted">
              {candidatesTotal} pages scanned
            </Badge>
            <Badge variant={dealsExtracted > 0 ? "default" : "secondary"} 
                   className={dealsExtracted > 0 ? "bg-green-600" : ""}>
              {dealsExtracted} deals extracted
            </Badge>
            <Badge variant={stagingCardsCreated > 0 ? "default" : "secondary"} 
                   className={stagingCardsCreated > 0 ? "bg-blue-600" : ""}>
              {stagingCardsCreated} staging cards
            </Badge>
          </div>
          
          {/* CR089: Model & Prompt Pack Proof */}
          <div className="text-xs text-muted-foreground space-y-1">
            <div className="flex items-center gap-2">
              <Zap className="h-3 w-3" />
              <span>Model: {aiModel}</span>
              {timingMs.total !== undefined && (
                <>
                  <span>·</span>
                  <Clock className="h-3 w-3" />
                  <span>~{formatDuration(timingMs.total)}</span>
                </>
              )}
            </div>
            {promptPackId && (
              <div className="flex items-center gap-2">
                <FileText className="h-3 w-3" />
                <span>Prompt Pack: {promptPackName || promptPackId}</span>
                {promptPackVersion && <span className="opacity-60">({promptPackVersion})</span>}
              </div>
            )}
            {!promptPackId && (
              <div className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="h-3 w-3" />
                <span>No prompt pack configured (using inline prompt)</span>
              </div>
            )}
            {/* CR475: Version stamp */}
            {diagnostics?.ai_scan_version && (
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-muted-foreground">
                  AI Scan Version: {diagnostics.ai_scan_version}
                </span>
              </div>
            )}
            {/* CR481: Watchdog auto-finalize badge */}
            {diagnostics?.watchdog_finalized && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-[10px]">
                  ⚡ Auto-finalized ({diagnostics.watchdog_reason || "watchdog"})
                </Badge>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CR092: Enhanced Reconciliation Summary */}
      {status !== "RUNNING" && diagnostics?.reconciliation && (
        <div className={`p-2 rounded-md text-xs ${
          diagnostics.reconciliation.mismatch_detected 
            ? "bg-destructive/10 border border-destructive/20" 
            : "bg-muted"
        }`}>
          <div className="flex items-center gap-2 font-medium">
            {diagnostics.reconciliation.mismatch_detected ? (
              <>
                <XCircle className="h-3 w-3 text-destructive" />
                <span className="text-destructive">Reconciliation FAILED (mismatch)</span>
              </>
            ) : (
              <>
                <CheckCircle className="h-3 w-3 text-green-600" />
                <span>Reconciliation OK</span>
              </>
            )}
          </div>
          <div className="mt-1 grid grid-cols-3 gap-2 text-muted-foreground">
            <span>Extracted: {diagnostics.reconciliation.extracted_deal_count}</span>
            <span>Inserted: {diagnostics.reconciliation.inserted_staging_deal_count}</span>
            <span>Rejected: {diagnostics.reconciliation.rejected_deal_count}</span>
          </div>
          {diagnostics.reconciliation.mismatch_diff !== undefined && (
            <div className="mt-1 text-destructive font-mono">
              Diff: {diagnostics.reconciliation.mismatch_diff} deals unaccounted for
            </div>
          )}
          {diagnostics.reconciliation.rejection_reasons.length > 0 && (
            <div className="mt-1">
              <span className="text-muted-foreground">Rejection reasons: </span>
              {diagnostics.reconciliation.rejection_reasons.map((r, i) => (
                <Badge key={i} variant="outline" className="text-[10px] mr-1">
                  {r.reason}: {r.count}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CR092: Actionable Errors - Expandable */}
      {warningErrors.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-2 text-amber-600 dark:text-amber-400 hover:text-amber-700 transition-colors">
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium">{warningErrors.length} warning(s)</span>
              <ChevronDown className="h-3 w-3" />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-2 max-h-60 overflow-auto">
              {warningErrors.map((err, idx) => (
                <div key={`warn-${idx}`} className="p-2 bg-amber-50 border border-amber-200 rounded text-xs dark:bg-amber-950/20 dark:border-amber-900/40">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{err.step_name}</Badge>
                      <span className="font-mono text-amber-700 dark:text-amber-300">{err.code}</span>
                    </div>
                    <span className="text-muted-foreground text-[10px]">
                      {err.timestamp ? new Date(err.timestamp).toLocaleTimeString() : "—"}
                    </span>
                  </div>
                  <p className="mt-1 text-foreground">{err.message}</p>
                  {(err.flyer_merchant || err.page_number) && (
                    <div className="mt-1 flex gap-2 text-muted-foreground">
                      {err.flyer_merchant && <span>Merchant: {err.flyer_merchant}</span>}
                      {err.page_number && <span>Page: #{err.page_number}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {blockingErrors.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-2 text-amber-600 dark:text-amber-400 hover:text-amber-700 transition-colors">
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium">{blockingErrors.length} error(s) occurred</span>
              <ChevronDown className="h-3 w-3" />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-2 max-h-60 overflow-auto">
              {blockingErrors.map((err, idx) => (
                <div key={idx} className="p-2 bg-destructive/5 border border-destructive/20 rounded text-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{err.step_name}</Badge>
                      <span className="font-mono text-destructive">{err.code}</span>
                    </div>
                    <span className="text-muted-foreground text-[10px]">
                      {err.timestamp ? new Date(err.timestamp).toLocaleTimeString() : "—"}
                    </span>
                  </div>
                  <p className="mt-1 text-foreground">{err.message}</p>
                  {(err.flyer_merchant || err.page_number) && (
                    <div className="mt-1 flex gap-2 text-muted-foreground">
                      {err.flyer_merchant && <span>Merchant: {err.flyer_merchant}</span>}
                      {err.page_number && <span>Page: #{err.page_number}</span>}
                      {err.ai_call_id && <span className="font-mono">AI Call: {err.ai_call_id}</span>}
                    </div>
                  )}
                  {err.stack_trace && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                        Stack trace
                      </summary>
                      <pre className="mt-1 p-1 bg-muted rounded text-[10px] overflow-auto max-h-32 font-mono">
                        {err.stack_trace}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* CR089: Per-Page Results Table */}
      {pageResults.length > 0 && (
        <Collapsible open={isTableOpen} onOpenChange={setIsTableOpen}>
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <ChevronDown className={`h-3 w-3 transition-transform ${isTableOpen ? "rotate-180" : ""}`} />
              <Zap className="h-3 w-3" />
              {pageResults.length} page{pageResults.length !== 1 ? "s" : ""} processed — View per-page results
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-2">
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs">
                      <TableHead className="w-[60px]">Page</TableHead>
                      <TableHead className="w-[80px]">Status</TableHead>
                      <TableHead className="w-[80px]">Deals</TableHead>
                      <TableHead>Deal Types</TableHead>
                      <TableHead className="w-[80px]">Time</TableHead>
                      <TableHead className="w-[60px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageResults.map((pageResult, idx) => {
                      // CR455: Use kept counts by default, parsed as context
                      const keptCount = pageResult.kept_deals_count ?? pageResult.staging_deals_created_count;
                      const parsedCount = pageResult.parsed_deals_count;
                      const keptBreakdown = pageResult.kept_deal_type_breakdown || pageResult.deal_type_breakdown || {};
                      const parsedBreakdown = pageResult.parsed_deal_type_breakdown || pageResult.deal_type_breakdown || {};
                      const rejectedCount = pageResult.rejected_count ?? 0;
                      const rejectReasons = pageResult.reject_reasons ?? [];
                      const hasParsedDiff = parsedCount > keptCount;
                      const printedPage = pageResult.printed_page_number;
                      
                      return (
                      <TableRow key={`${pageResult.page_number}-${idx}`} className="text-xs">
                        <TableCell className="font-medium">
                          <div className="flex flex-col">
                            <span>#{pageResult.page_number}</span>
                            {printedPage != null && (
                              <span className="text-[10px] text-muted-foreground" title="Printed page # from flyer (reference only, not used for joins)">
                                Print: {printedPage}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {getStatusIcon(pageResult.ai_status)}
                            <span className="capitalize">{pageResult.ai_status.toLowerCase()}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            <span className="font-medium">{keptCount}</span>
                            {hasParsedDiff && (
                              <span className="text-[10px] text-muted-foreground">Parsed: {parsedCount}</span>
                            )}
                            {rejectedCount > 0 && (
                              <span className="text-[10px] text-amber-600" title={rejectReasons.join(', ')}>
                                Rejected: {rejectedCount}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {getDealTypeBadges(keptBreakdown)}
                            {Object.keys(keptBreakdown).length === 0 && hasParsedDiff && (
                              <span className="text-[10px] text-muted-foreground" title={`Parsed: ${Object.entries(parsedBreakdown).map(([t,c]) => `${t}:${c}`).join(', ')}`}>
                                (parsed: {Object.entries(parsedBreakdown).map(([t,c]) => `${t}:${c}`).join(', ')})
                              </span>
                            )}
                            {Object.keys(keptBreakdown).length === 0 && !hasParsedDiff && !pageResult.raw_ai_response_json && (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{formatDuration(pageResult.duration_ms)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => setSelectedPageForRawView(pageResult)}
                            title="View AI Output"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* CR481: View in Staging button — deterministic CTA keyed off batch_run_id */}
              {/* Show when deals extracted OR staging cards created */}
              {(stagingCardsCreated > 0 || dealsExtracted > 0) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    // CR482E: Use FLYER source_type (matches staging insert) + toast on failure
                    const target = `/admin/batch-runs/${batchRunId}?tab=staging&sourceFilter=FLYER`;
                    console.log(`[CR482] Navigating to staging: ${target}`);
                    try {
                      navigate(target);
                    } catch (err) {
                      console.error("[CR482] Navigation failed:", err);
                    }
                  }}
                >
                  <ExternalLink className="h-3 w-3 mr-2" />
                  {stagingCardsCreated > 0
                    ? `View ${stagingCardsCreated} Extracted Deals in Staging`
                    : `View ${dealsExtracted} Extracted Deals (not yet staged)`}
                </Button>
              )}
              {/* CR481: Banner when deals extracted but none staged */}
              {dealsExtracted > 0 && stagingCardsCreated === 0 && (
                <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md text-xs text-amber-700 dark:text-amber-300">
                  <AlertCircle className="h-3 w-3 shrink-0" />
                  <span>Deals extracted but not staged — check reconciliation below.</span>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* CR089: Raw AI Output Dialog */}
      <Dialog open={!!selectedPageForRawView} onOpenChange={(open) => !open && setSelectedPageForRawView(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>AI Output — Page #{selectedPageForRawView?.page_number}</DialogTitle>
            <DialogDescription>
              Full evidence and raw AI response for this page scan
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {selectedPageForRawView && (
              <div className="space-y-4 text-sm">
                {/* Proof Artifacts */}
                <div className="space-y-2">
                  <h4 className="font-semibold">Proof Artifacts</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs bg-muted p-3 rounded-md">
                    <div>
                      <span className="text-muted-foreground">Page Image:</span>
                      <p className="font-mono break-all">{selectedPageForRawView.page_storage_path}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Model Used:</span>
                      <p className="font-mono">{selectedPageForRawView.model_used}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Prompt Pack ID:</span>
                      <p className="font-mono">{selectedPageForRawView.prompt_pack_id_used || "(inline prompt)"}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Prompt Pack Version:</span>
                      <p className="font-mono">{selectedPageForRawView.prompt_pack_version_used || "—"}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Duration:</span>
                      <p className="font-mono">{formatDuration(selectedPageForRawView.duration_ms)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status:</span>
                      <p className="font-mono">{selectedPageForRawView.ai_status}</p>
                    </div>
                  </div>
                </div>

                {/* Extraction Summary */}
                <div className="space-y-2">
                  <h4 className="font-semibold">Extraction Summary</h4>
                  <div className="flex gap-2">
                    <Badge variant="outline">Parsed: {selectedPageForRawView.parsed_deals_count} deals</Badge>
                    <Badge variant="outline">Staged: {selectedPageForRawView.staging_deals_created_count} cards</Badge>
                    {selectedPageForRawView.parsed_deals_count !== selectedPageForRawView.staging_deals_created_count && (
                      <Badge variant="destructive">MISMATCH</Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {getDealTypeBadges(selectedPageForRawView.deal_type_breakdown, selectedPageForRawView.raw_ai_response_json)}
                  </div>
                </div>

                {/* Error if any */}
                {selectedPageForRawView.error && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-destructive">Error</h4>
                    <div className="bg-destructive/10 text-destructive p-3 rounded-md text-xs font-mono">
                      {selectedPageForRawView.error}
                    </div>
                  </div>
                )}

                {/* CR227: Parse Debug Info - shows raw AI response shape before coercion */}
                {(selectedPageForRawView.raw_ai_response_json as { parse_debug?: { rawText_type?: string; rawText_keys?: string[]; rawText_preview?: string; coerced_from?: string }; parse_status?: string; parse_error?: string })?.parse_debug && (
                  <div className="space-y-2">
                    <h4 className="font-semibold">Parse Debug Info</h4>
                    <div className="bg-muted p-3 rounded-md text-xs space-y-2">
                      {/* Parse status */}
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Status:</span>
                        <Badge variant={(selectedPageForRawView.raw_ai_response_json as { parse_status?: string }).parse_status === "SUCCESS" ? "outline" : "destructive"}>
                          {(selectedPageForRawView.raw_ai_response_json as { parse_status?: string }).parse_status || "UNKNOWN"}
                        </Badge>
                        {(selectedPageForRawView.raw_ai_response_json as { parse_error?: string }).parse_error && (
                          <span className="text-destructive text-[10px] font-mono truncate max-w-[200px]">
                            {(selectedPageForRawView.raw_ai_response_json as { parse_error?: string }).parse_error}
                          </span>
                        )}
                      </div>
                      {/* Raw shape info */}
                      <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                        <div>
                          <span className="text-foreground font-medium">Type:</span>{" "}
                          <code className="text-[10px] bg-background px-1 rounded">{(selectedPageForRawView.raw_ai_response_json as { parse_debug?: { rawText_type?: string } }).parse_debug?.rawText_type}</code>
                        </div>
                        <div>
                          <span className="text-foreground font-medium">Coerced from:</span>{" "}
                          <code className="text-[10px] bg-background px-1 rounded">{(selectedPageForRawView.raw_ai_response_json as { parse_debug?: { coerced_from?: string } }).parse_debug?.coerced_from}</code>
                        </div>
                      </div>
                      {/* Keys if object */}
                      {(selectedPageForRawView.raw_ai_response_json as { parse_debug?: { rawText_keys?: string[] } }).parse_debug?.rawText_keys && (
                        <div>
                          <span className="text-foreground font-medium">Object Keys:</span>{" "}
                          <code className="text-[10px] bg-background px-1 rounded break-all">
                            {(selectedPageForRawView.raw_ai_response_json as { parse_debug?: { rawText_keys?: string[] } }).parse_debug?.rawText_keys?.join(", ")}
                          </code>
                        </div>
                      )}
                      {/* Raw preview before coercion */}
                      <details className="mt-1">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground font-medium">
                          Raw Content Preview (before coercion)
                        </summary>
                        <pre className="mt-1 p-2 bg-background rounded text-[10px] overflow-auto max-h-40 font-mono whitespace-pre-wrap">
                          {(selectedPageForRawView.raw_ai_response_json as { parse_debug?: { rawText_preview?: string } }).parse_debug?.rawText_preview || "N/A"}
                        </pre>
                      </details>
                    </div>
                  </div>
                )}

                {/* Token usage from model_response */}
                {(selectedPageForRawView.raw_ai_response_json as { model_response?: { usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number } } })?.model_response?.usage && (
                  <div className="space-y-2">
                    <h4 className="font-semibold">Token Usage</h4>
                    <div className="flex gap-2">
                      <Badge variant="outline" className="text-[10px]">
                        Input: {(selectedPageForRawView.raw_ai_response_json as { model_response?: { usage?: { prompt_tokens?: number } } }).model_response?.usage?.prompt_tokens ?? "—"}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">
                        Output: {(selectedPageForRawView.raw_ai_response_json as { model_response?: { usage?: { completion_tokens?: number } } }).model_response?.usage?.completion_tokens ?? "—"}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">
                        Total: {(selectedPageForRawView.raw_ai_response_json as { model_response?: { usage?: { total_tokens?: number } } }).model_response?.usage?.total_tokens ?? "—"}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Raw AI Response */}
                <div className="space-y-2">
                  <h4 className="font-semibold">Raw AI Response JSON</h4>
                  <pre className="bg-muted p-3 rounded-md text-xs overflow-auto max-h-[300px] font-mono">
                    {JSON.stringify(selectedPageForRawView.raw_ai_response_json, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
